import React, { useState, useEffect } from 'react';
import type { FC, FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router';

import { InvitationAPIs, LoginLinkAPIs, TokenTypes } from '@/api';
import { Button, ButtonSize, ButtonType } from '@/common/button';
import { Carousel } from '@/common/carousel';
import { CarouselSlide } from '@/common/carousel-slide';
import { CustomToast } from '@/common/custom-toast';
import { IntaglioLogo } from '@/common/intaglio-logo';
import animData1 from '@/common/lottie/slide-1.json';
import animData2 from '@/common/lottie/slide-2.json';
import animData3 from '@/common/lottie/slide-3.json';
import { TextField, TextFieldType } from '@/common/text-field';
import { useAuth } from '@/hooks/useAuth';

export const Invite: FC = () => {
    const { loginViaInvite, logout } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [token] = useState(searchParams.get('token') || null);
    const [isTokenValid, setIsTokenValid] = useState(false);
    const [userEmail, setUserEmail] = useState('');
    const [firstNameHasError, setFirstNameHasError] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastNameHasError, setLastNameHasError] = useState(false);
    const [lastName, setLastName] = useState('');

    useEffect(() => {
        if (token) {
            const validateToken = async (token: string) => {
                try {
                    const data = (await InvitationAPIs.validateInviteToken(token)) as { email: string };
                    setIsTokenValid(true);
                    setUserEmail(data.email);
                } catch (e) {
                    CustomToast.error('Invitation seems to be invalid.');
                    console.error(e);
                }
            };
            validateToken(token);
        }
    }, [token]);

    const handleCreateAccount = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();

        setFirstNameHasError(!firstName);
        setLastNameHasError(!lastName);

        if (!firstName || !lastName || !token) {
            return;
        }

        try {
            logout();
            await loginViaInvite(token, firstName, lastName);
            await navigate('/templates');
        } catch (error) {
            CustomToast.error('Invitation seems to be invalid.');
        }
    };

    return (
        <div className="min-h-screen flex flex-row bg-black-50">
            <div className="md:w-2/5 w-full flex flex-col px-24 py-16">
                <div className="w-[400px] h-full flex flex-col justify-evenly">
                    <IntaglioLogo className="mt-6" />
                    <div className="h-full w-full items-center justify-center flex flex-col gap-5">
                        <p className="text-lg text-white font-normal w-[400px]">
                            Finish creating your account for {userEmail}
                        </p>
                        <form onSubmit={handleCreateAccount} className="flex flex-col w-[400px] gap-2">
                            <div className="flex flex-row gap-2">
                                <div className="w-full">
                                    <label
                                        className="block text-white text-sm font-normal mb-2"
                                        htmlFor="firstNameField"
                                    >
                                        First Name
                                    </label>
                                    <TextField
                                        id="firstNameField"
                                        type={TextFieldType.Text}
                                        required={true}
                                        value={firstName}
                                        onChange={(value) => {
                                            setFirstName(value);
                                        }}
                                        placeholder="Type Here"
                                        hasError={firstNameHasError}
                                        errorMessage="First name is required"
                                    />
                                </div>
                                <div className="w-full">
                                    <label
                                        className="block text-white text-sm font-normal mb-2"
                                        htmlFor="lastNameField"
                                    >
                                        Last Name
                                    </label>
                                    <TextField
                                        id="lastNameField"
                                        type={TextFieldType.Text}
                                        required={true}
                                        value={lastName}
                                        onChange={(value) => {
                                            setLastName(value);
                                        }}
                                        placeholder="Type Here"
                                        hasError={lastNameHasError}
                                        errorMessage="Last name is required"
                                    />
                                </div>
                            </div>
                            <div className="flex items-center justify-between mt-2 w-full">
                                <Button
                                    type="submit"
                                    title="Create Account"
                                    disabled={!isTokenValid}
                                    buttonType={ButtonType.PRIMARY}
                                    buttonSize={ButtonSize.LARGE}
                                    className="w-full"
                                />
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <div className="w-3/5 gradient-background hidden md:block">
                <div className="min-h-screen flex items-center justify-center">
                    <Carousel
                        autoPlay={true}
                        interval={3000}
                        slideTexts={[
                            'Templatize designs from any platform',
                            'Set design rules that cannot be broken',
                            'Review and replace assets when needed',
                        ]}
                    >
                        <CarouselSlide key={'slide1'} animData={animData1} />
                        <CarouselSlide key={'slide2'} animData={animData2} />
                        <CarouselSlide key={'slide3'} animData={animData3} />
                    </Carousel>
                </div>
            </div>
        </div>
    );
};
